/** API base + voice WebSocket URL. Platform-tier. The booth kiosk has no public access gate
 *  (RESKIN §3) — the kiosk URL is private; per-event setup is behind the admin passcode. */

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/**
 * The voice WebSocket endpoint (ADR-002 / item 1 — replaces the LiveKit token fetch). Same origin
 * as the API, ws(s) scheme: → VoiceSession Durable Object → Pipecat container.
 *
 * R18 W1-4 (voice-client-contract.md §1): callers MUST supply a fresh `crypto.randomUUID()` per
 * connect() so each visit gets its own Durable Object container. The `session` parameter has no
 * default — omitting it falls back to a server-minted UUID (safe, but prevents W2-1 reconnect
 * resume). `useVoice.ts` always supplies a per-connect sessionKey.
 */
export function voiceWsUrl(session?: string): string {
  const base = API_BASE || (typeof location !== 'undefined' ? location.origin : '');
  const ws = base.replace(/^http/, 'ws');
  const qs = session ? `?session=${encodeURIComponent(session)}` : '';
  return `${ws}/v1/ws${qs}`;
}

/**
 * R18 Wave 1.6 — Warm-up handshake (voice-client-contract.md §7).
 *
 * Polls GET /v1/session/warmup?session=<uuid> every `intervalMs` until the container is ready
 * (200 → {ready:true}) or the deadline elapses. Returns `true` when ready, throws
 * `'warmup-timeout'` on deadline exceeded.
 *
 * CRITICAL uuid guarantee: the caller mints ONE uuid and passes the same value here AND to
 * `voiceWsUrl()`. Both routes call `routeToVoiceSession(env, req, sessionKey)` which does
 * `getContainer(env.VOICE_SESSION, sessionKey)` — one uuid → one DO → one container. Warmup
 * fires the container boot; WS arrives at an already-warm port.
 *
 * @param sessionKey  The uuid already minted for this session (shared with voiceWsUrl).
 * @param signal      AbortSignal — the caller can cancel mid-poll (e.g. user hits "Start over").
 * @param intervalMs  Poll interval (default 2 000ms per contract).
 * @param budgetMs    Overall deadline (default 55 000ms — middle of the 45-60s window).
 */
export async function warmupSession(
  sessionKey: string,
  signal?: AbortSignal,
  intervalMs = 2_000,
  budgetMs = 55_000
): Promise<true> {
  const url = `${API_BASE}/v1/session/warmup?session=${encodeURIComponent(sessionKey)}`;
  const deadline = Date.now() + budgetMs;

  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException('Warmup aborted', 'AbortError');

    try {
      const resp = await fetch(url, { signal });
      if (resp.ok) {
        // 200 → {ready:true} — container is warm; proceed to open the voice WS.
        return true;
      }
      // 503 → {ready:false, starting:true} — still booting; keep polling.
    } catch (err) {
      // Network error or aborted — if aborted, propagate; otherwise keep polling.
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      // Transient network error: wait and retry within budget.
    }

    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, intervalMs);
      signal?.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new DOMException('Warmup aborted', 'AbortError'));
      });
    });
  }

  throw new Error('warmup-timeout');
}

/**
 * POST /v1/kiosk/style-snapshot
 * Persists the visitor's declared Style Snapshot preferences (conversation-design.md §6c).
 *
 * The kiosk PWA calls this directly at the §6c close beats (post_shortlist, post_spec, farewell).
 * No HMAC required — auth is by conversation_id (received from the server via ui.session on connect).
 * Endpoint: services/api/src/routes/kiosk/style_snapshot.ts
 * Contract: docs/declared-prefs-contract.md §Kiosk path
 *
 * Non-blocking — a snapshot failure must never break the session end flow.
 */
export type StyleSnapshotPayload = {
  conversation_id: string;
  finishes?: string[];
  materials?: string[];
  colour_tones?: string[];
  styles?: string[];
  size_classes?: string[];
  use_cases?: string[];
  session_beat?: 'pre_browse' | 'post_shortlist' | 'post_spec' | 'farewell' | 'async';
  cx_rating?: number;
  cx_comment?: string;
};

export async function submitStyleSnapshot(
  conversationId: string | undefined,
  facets: Omit<StyleSnapshotPayload, 'conversation_id'> = {}
): Promise<void> {
  if (!conversationId) return;
  try {
    const payload: StyleSnapshotPayload = { conversation_id: conversationId, ...facets };
    const res = await fetch(`${API_BASE}/v1/kiosk/style-snapshot`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-conversation-id': conversationId,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn('[style-snapshot] unexpected status', res.status);
    }
  } catch (err) {
    console.warn('[style-snapshot] failed (non-blocking):', err);
  }
}

/**
 * POST /v1/kiosk/card-scan — business-card scan-to-fill (R10, S1 welcome card, §4.2 hero path).
 *
 * The visitor holds up their business card; the kiosk captures a photo (camera/file) and posts
 * it here. The server runs vision-model extraction and returns the fields it could read plus a
 * confidence score. The card image is extract-and-discard by default (retention is a client
 * decision — Q13); the kiosk never stores it.
 *
 * Auth: conversation-id-scoped like the other kiosk calls (x-conversation-id header + query),
 * matching /v1/kiosk/style-snapshot and /v1/card. The handoff calls this "session-HMAC" — the
 * repo's kiosk calls are conversation-id-scoped today, so this follows that; flagged for Andy in
 * case Butler's R26 server half lands a true HMAC (then this header set is upgraded, not changed).
 *
 * Contract (voice-capture-contract.md, R26 — concierge-gg owns the response shape):
 *   200 → { fields: { name?, company?, email?, phone? }, confidence: number }
 * The welcome card only *uses* name + company (§4.1); extra fields (email/phone) are returned for
 * the close/S7 card and ignored here.
 *
 * ⚑ ADAPTER: server route is concierge-gg R26. Until it lands, a 404/501 surfaces as a thrown
 * error the caller handles gracefully (fall back to typing) — never blocks the visitor.
 */
export type CardScanResult = {
  fields: { name?: string; company?: string; email?: string; phone?: string };
  confidence: number;
};

export async function scanBusinessCard(
  file: Blob,
  conversationId: string | undefined
): Promise<CardScanResult> {
  const qs = conversationId ? `?conversation=${encodeURIComponent(conversationId)}` : '';
  const headers: Record<string, string> = {
    'content-type': file.type || 'application/octet-stream',
  };
  if (conversationId) headers['x-conversation-id'] = conversationId;
  const res = await fetch(`${API_BASE}/v1/kiosk/card-scan${qs}`, {
    method: 'POST',
    headers,
    body: file,
  });
  if (!res.ok) throw new Error(`card-scan failed (${res.status})`);
  const data = (await res.json()) as Partial<CardScanResult>;
  return {
    fields: data.fields ?? {},
    confidence: typeof data.confidence === 'number' ? data.confidence : 0,
  };
}
